import AlarmsTableFactory from "../factories/AlarmsTableFactory";
import Controller from "@/controllers/index";
import DateTimeHandlerService from "@/services/common/date-time/DateTimeHandlerService";
import i18n from "@/i18n";
import AlarmManagementStatus from "@/constants/enums/AlarmManagementStatus";

/**
 * Busca a lista do log de alarmes.
 * @param startDateTime  Data de inicio da pesquisa
 * @param endDateTime  Data do fim da pesquisa
 * @returns {Array} Lista de log das notificações.
 */
const GetAlarmsLog = async function(meIds = [], startDateTime = "", endDateTime = "", status = [], managements = [])
{
	const resp = await Controller.NotificationLogging.Get(meIds, startDateTime, endDateTime, status, managements);

	const rawLogs = Array.isArray(resp) ? resp : (resp?.data || []);

	if (rawLogs.length === 0) return [];

	const logs = rawLogs.map((log) =>
	{
		return AlarmsTableFactory.LogView(log);
	});

	logs.forEach(l =>
	{
		l.formattedValue = l?.value == null ? null : Number(l.value).toFixed(6);
	});

	return logs;
};

/**
 * Atualiza os status de gerenciamento dos alarmes.
 *
 * @param {number} management - Novo status de gerenciamento a ser aplicado.
 * @param {number} expirationDate - Data de expiração para a quarentena representada por um inteiro.
 * @param {number} id- Identificador do alarme a ser atualizado
 */
const UpdateAlarmsManagements = async function(management = AlarmManagementStatus.NONE, expirationDate = null, id = 0)
{
	const resp = await Controller.NotificationLogging.UpdateManagements(management, expirationDate, id);
	return resp.status = 201 ? resp.data : [];
};

/**
 * Faz o download de um relatório de forma assíncrona.
 *
 * Chama o controlador para baixar o relatório a partir do arquivo fornecido
 * e retorna os dados do relatório se a resposta for bem-sucedida.
 *
 * @param {string} payload.notificationLoggingsId - O identificador da notificação.
 * @param {string} payload.id - O identificador do relatório a ser baixado.
 */
const DownloadReport = async function(payload)
{
	const resp = await Controller.NotificationLogging.DownloadReport(payload);
	return resp.status === 200 ? resp.data : "";
};

/**
 * Faz o upload de um relatório de forma assíncrona.
 *
 * Chama o controlador para enviar os dados do relatório 
 * e retorna os dados da resposta se a operação for bem-sucedida.
 *
 * @param {File} payload.reportFile - O arquivo do relatório a ser enviado.
 * @param {string} payload.loggindId - O identificador da notificação para anexar o relatório.
 */
const UpdloadReport = async function(payload)
{
	const resp = await Controller.NotificationLogging.UploadReport(payload);
	return resp.status === 201 ? resp.data : {};
};

/**
 * Ordena um array de objetos com base em vários campos.
 */
const TableSort = function(items = [ {} ], fields = [ "" ], isDesc = [ false ])
{
	if (!fields) return items;

	items.sort((a, b) =>
	{
		let result = 0;

		for (let i = 0; i < fields.length; i++)
		{
			const field = fields[i];
			const sortParms = [ a[field], b[field], isDesc[i] ];

			if (field === "dateTime" || field === "formattedDateTime")
			{
				result = SortTableByDate(...sortParms);
			}
			else if (typeof a[field] !== "undefined")
			{
				result = SortTableByStr(...sortParms);
			}
			if (result !== 0) return result;
		}
		return 0;
	});
	return items;
};

const SortTableByDate = function(dateA = "", dateB = "", isDesc = false)
{
	const dateTimeFormat = i18n.locale === "en-US" ? "MM/DD/YYYY hh:mm:ss A" : "DD/MM/YYYY HH:mm:ss";
	const firstDate = DateTimeHandlerService.GetMomentObject(dateA, dateTimeFormat);
	const secondDate = DateTimeHandlerService.GetMomentObject(dateB, dateTimeFormat);
	if (isDesc)
		return secondDate - firstDate;

	return firstDate - secondDate;
};

const SortTableByStr = function(strA = "", strB = "", isDesc = false)
{
	const normalizedA = String(strA ?? "").toLowerCase();
	const normalizedB = String(strB ?? "").toLowerCase();
	const compareOptions = { ignorePunctuation: true };

	if (isDesc)
		return normalizedB.localeCompare(normalizedA, undefined, compareOptions);

	return normalizedA.localeCompare(normalizedB, undefined, compareOptions);
};

/**
 * Retorna o nome da tratativa baseado no status fornecido.
 *
 * @param {number} management - O código do status da tratativa.
 * @returns {string} O nome descritivo da tratativa.
 */
function GetManagementName(management = AlarmManagementStatus.NONE)
{
	switch (management)
	{
	case AlarmManagementStatus.NONE:
		return i18n.t("Labels.NoneTratative");
	case AlarmManagementStatus.SEEN:
		return i18n.t("Labels.SeenTratative");
	case AlarmManagementStatus.DISMISSED:
		return i18n.t("Labels.DismissedTratative");
	case AlarmManagementStatus.QUARENTINE:
		return i18n.t("Labels.QuarentineTratative");
	case AlarmManagementStatus.REPORT_ATTACHED:
		return i18n.t("Labels.ReportTratative");
	case AlarmManagementStatus.EXPIRED_QUARENTINE:
		return i18n.t("Labels.ExpiredQuarentineTratative");
	default:
		return " - ";
	}
}

/**
 * Retorna o ícone correspondente ao status de gerenciamento do alarme.
 *
 * @param {number} management Status do gerenciamento do alarme.
 * @returns {string} Código do ícone Material Design (mdi) referente ao status informado.
 */
function GetManagementIcon(management = AlarmManagementStatus.NONE)
{
	switch (management)
	{
	case AlarmManagementStatus.NONE:
		return "mdi-eye-off-outline";
	case AlarmManagementStatus.SEEN:
		return "mdi-eye-outline";
	case AlarmManagementStatus.DISMISSED:
		return "mdi-close";
	case AlarmManagementStatus.QUARENTINE:
		return "mdi-alert-outline";
	case AlarmManagementStatus.REPORT_ATTACHED:
		return "mdi-file-document-outline";
	case AlarmManagementStatus.EXPIRED_QUARENTINE:
		return "mdi-history";
	default:
		return "mdi-help-circle-outline";
	}
}

/**
 * Retorna a classe CSS para a cor da tratativa com base no status.
 *
 * @param {number} management - O código do status da tratativa.
 * @returns {string} A classe CSS aplicada para a cor da tratativa.
 */
function GetExpiredQuarentineColor(management = AlarmManagementStatus.NONE)
{
	return management === AlarmManagementStatus.EXPIRED_QUARENTINE ? "error--text" : "";
}

/**
 * Cria a rota de redirecionamento para a tela de tendências.
 * @param {Object} item - Item da tabela.
 * @param {String} lang - Idioma atual da rota.
 * @returns {Object} Rota de tendências com os parâmetros necessários.
 */
const CreateTrendURL = function(item, lang)
{
	const {
		formattedDateTime, monitoringElementId, trendSignalSettingId, trendFrequencyId
	} = item;

	const isoDateTime = DateTimeHandlerService.ToUTCDateTime(formattedDateTime);

	let url = {
		name:   "WindHomeTrends",
		params: { lang },
		query:  {
			assets:           true,
			turbineId:        monitoringElementId,
			notificationDate: isoDateTime,
		}
	};

	if (!!trendSignalSettingId) url.query.trendSignalId = trendSignalSettingId;
	if (!!trendFrequencyId) url.query.trendFrequencyId = trendFrequencyId;

	return url;
};

export default {
	GetAlarmsLog,
	TableSort,
	CreateTrendURL,
	GetManagementName,
	GetExpiredQuarentineColor,
	GetManagementIcon,
	UpdateAlarmsManagements,
	DownloadReport,
	UpdloadReport
};
